import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../Spinner';
import PermissionManagerModal from './PermissionManagerModal';

export default function UserManagementTable() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Permission modal state
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Suspension state
  const [suspendingUserId, setSuspendingUserId] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null);

  // Context menu state
  const [openMenuUserId, setOpenMenuUserId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuUserId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openMenu = (userId, event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.right - 192 // 192px = w-48 menu width
    });
    setOpenMenuUserId(openMenuUserId === userId ? null : userId);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });

      if (search) params.append('search', search);
      if (roleFilter !== 'all') params.append('role_filter', roleFilter);

      const response = await fetch(`${API_URL}/admin/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Errore nel caricamento degli utenti');
      }

      const data = await response.json();
      setUsers(data.users);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, page, perPage, search, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // Reset to page 1 on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSuspend = async () => {
    if (!suspendTarget) return;
    setSuspendingUserId(suspendTarget.id);

    try {
      const response = await fetch(`${API_URL}/admin/users/${suspendTarget.id}/suspend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: suspendReason || null })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante la sospensione');
      }

      setShowSuspendDialog(false);
      setSuspendTarget(null);
      setSuspendReason('');
      fetchUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setSuspendingUserId(null);
    }
  };

  const handleUnsuspend = async (user) => {
    if (!window.confirm(`Rimuovere la sospensione per ${user.nome} ${user.cognome}?`)) return;
    setSuspendingUserId(user.id);

    try {
      const response = await fetch(`${API_URL}/admin/users/${user.id}/unsuspend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante la rimozione della sospensione');
      }

      fetchUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setSuspendingUserId(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/users/export-csv`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Errore durante l\'esportazione');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Utenti_Export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message);
    }
  };

  const RoleBadge = ({ user }) => {
    const badges = [];

    if (user.is_owner) {
      badges.push(
        <span key="owner" className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
          Proprietario
        </span>
      );
    }
    if (user.is_admin) {
      badges.push(
        <span key="admin" className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
          Amministratore
        </span>
      );
    }
    if (user.is_organizer) {
      badges.push(
        <span key="organizer" className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
          Organizzatore
        </span>
      );
    }
    if (badges.length === 0) {
      badges.push(
        <span key="user" className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
          Utente
        </span>
      );
    }

    return <div className="flex gap-1 flex-wrap">{badges}</div>;
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-3">
        <div className="flex gap-3 flex-wrap items-center">
          <input
            type="text"
            placeholder="Cerca per nome, email, telefono, data di nascita..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 min-w-[300px] px-3 py-2 border rounded"
          />
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border rounded"
          >
            <option value="all">Tutti gli utenti</option>
            <option value="admin">Solo amministratori</option>
            <option value="organizer">Solo organizzatori</option>
            <option value="owner">Solo proprietari</option>
            <option value="suspended">Solo sospesi</option>
          </select>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Esporta CSV
          </button>
        </div>

        <div className="text-sm text-gray-600">
          Mostrando {users.length} di {total} utenti
        </div>
      </div>

      {/* Table */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Telefono</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Data di nascita</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Ruoli</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Stato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{user.id}</td>
                  <td className="px-4 py-3 text-sm font-medium">
                    {user.nome} {user.cognome}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.numero_telefono || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {user.data_nascita ? new Date(user.data_nascita).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <RoleBadge user={user} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {user.is_suspended ? (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800">
                          Sospeso
                        </span>
                      ) : user.email_verified ? (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                          Attivo
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
                          Non verificato
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => openMenu(user.id, e)}
                      className="p-2 hover:bg-gray-100 rounded-full text-gray-600"
                      aria-label="Apri menu azioni"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white p-4 rounded-lg shadow flex justify-between items-center">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Per pagina:</label>
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            className="px-2 py-1 border rounded"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Precedente
          </button>
          <span className="text-sm text-gray-600">
            Pagina {page} di {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Successiva
          </button>
        </div>
      </div>

      {/* Permission Manager Modal */}
      <PermissionManagerModal
        show={showPermissionModal}
        onHide={() => {
          setShowPermissionModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSave={() => {
          // Refresh user list to show updated roles
          fetchUsers();
        }}
      />

      {/* Context Menu Portal */}
      {openMenuUserId && createPortal(
        <div
          ref={menuRef}
          className="fixed w-48 bg-white rounded-md shadow-lg border z-50"
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          <div className="py-1">
            <button
              onClick={() => {
                const user = users.find(u => u.id === openMenuUserId);
                setSelectedUser(user);
                setShowPermissionModal(true);
                setOpenMenuUserId(null);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Gestisci permessi
            </button>
            {(() => {
              const user = users.find(u => u.id === openMenuUserId);
              if (!user) return null;
              if (user.is_suspended) {
                return (
                  <button
                    onClick={() => {
                      setOpenMenuUserId(null);
                      handleUnsuspend(user);
                    }}
                    disabled={suspendingUserId === user.id}
                    className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 flex items-center gap-2 disabled:opacity-50"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {suspendingUserId === user.id ? 'Attendi...' : 'Riattiva utente'}
                  </button>
                );
              } else if (!user.is_admin && !user.is_owner) {
                return (
                  <button
                    onClick={() => {
                      setSuspendTarget(user);
                      setSuspendReason('');
                      setShowSuspendDialog(true);
                      setOpenMenuUserId(null);
                    }}
                    disabled={suspendingUserId === user.id}
                    className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    Sospendi utente
                  </button>
                );
              }
              return null;
            })()}
          </div>
        </div>,
        document.body
      )}

      {/* Suspend Confirmation Dialog */}
      {showSuspendDialog && suspendTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Sospendi utente
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Stai per sospendere <strong>{suspendTarget.nome} {suspendTarget.cognome}</strong> ({suspendTarget.email}).
              L'utente non potrà più accedere al sistema.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo (opzionale)
              </label>
              <textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
                rows={3}
                placeholder="Es: Comportamento inappropriato, account sospetto..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowSuspendDialog(false);
                  setSuspendTarget(null);
                  setSuspendReason('');
                }}
                className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleSuspend}
                disabled={suspendingUserId === suspendTarget.id}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {suspendingUserId === suspendTarget.id ? 'Sospensione...' : 'Conferma sospensione'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
